<script>
import APICalendarEvents from '@app/services/API/CalendarEvents';
import CalendarEventItem from '@app/components/CalendarEventItem.vue';
import CalendarEventFormModal from '@app/components/CalendarEventFormModal.vue';
import CalendarEventDetailModal from '@app/components/CalendarEventDetailModal.vue';
import CalendarGrid from '@shared/components/CalendarGrid.vue';

const STATUS_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'DRAFT', label: 'Brouillon' },
  { value: 'PUBLISHED', label: 'Publié' },
  { value: 'CANCELLED', label: 'Annulé' },
];

const FREQUENCY_OPTIONS = [
  { value: '', label: 'Toutes' },
  { value: 'once', label: 'Unique' },
  { value: 'recurring', label: 'Récurrent' },
];

const SORT_OPTIONS = [
  { value: 'start_date', label: 'Date de l\'événement' },
  { value: 'created_at', label: 'Date de création' },
  { value: 'title', label: 'Nom' },
];

const PER_PAGE = 20;

export default {
  components: { CalendarEventItem, CalendarGrid },
  head: {
    title: 'Calendrier',
  },
  data() {
    const now = this.$moment();
    return {
      events: [],
      isFetching: false,
      searchQuery: '',
      statusFilter: '',
      frequencyFilter: '',
      dateRange: [],
      sortBy: 'start_date',
      sortDir: 'desc',
      showFilters: false,
      viewMode: 'calendar',
      calendarMonth: now.month(),
      calendarYear: now.year(),
      currentPage: 1,
      STATUS_OPTIONS,
      FREQUENCY_OPTIONS,
      SORT_OPTIONS,
      PER_PAGE,
    };
  },
  computed: {
    paginatedEvents() {
      const start = (this.currentPage - 1) * PER_PAGE;
      return this.events.slice(start, start + PER_PAGE);
    },
  },
  created() {
    this.fetchEvents();
    this.$root.$on('calendar:refresh', this.fetchEvents);
  },
  beforeDestroy() {
    this.$root.$off('calendar:refresh', this.fetchEvents);
  },
  methods: {
    async fetchEvents() {
      this.isFetching = true;
      try {
        const [dateFrom, dateTo] = this.dateRange || [];
        const params = {
          q: this.searchQuery || undefined,
          status: this.statusFilter || undefined,
          frequency: this.frequencyFilter || undefined,
          date_from: dateFrom
            ? this.$moment(dateFrom).format('YYYY-MM-DD 00:00:00')
            : undefined,
          date_to: dateTo
            ? this.$moment(dateTo).format('YYYY-MM-DD 23:59:59')
            : undefined,
          sort_by: this.sortBy,
          sort_dir: this.sortDir,
        };
        const { data } = await APICalendarEvents.getCalendarEvents(params);
        this.events = data;
        this.currentPage = 1;
      } catch (e) {
        this.$buefy.toast.open({
          message: 'Erreur lors du chargement des événements',
          type: 'is-danger',
        });
      } finally {
        this.isFetching = false;
      }
    },
    openCreateModal() {
      this.$emit('create');
    },
    openEditModal(event) {
      this.$buefy.modal.open({
        parent: this,
        component: CalendarEventFormModal,
        hasModalCard: true,
        trapFocus: true,
        props: { event },
        events: {
          saved: () => this.fetchEvents(),
        },
      });
    },
    openDetailModal(event) {
      this.$buefy.modal.open({
        parent: this,
        component: CalendarEventDetailModal,
        hasModalCard: true,
        trapFocus: true,
        props: { event },
        events: {
          edit: (ev) => this.openEditModal(ev),
        },
      });
    },
    onCalendarMonthChange({ month, year }) {
      this.calendarMonth = month;
      this.calendarYear = year;
    },
    toggleSortDir() {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      this.fetchEvents();
    },
    onSortChange() {
      this.fetchEvents();
    },
    onFiltersChange() {
      this.fetchEvents();
    },
    onDateChange() {
      this.fetchEvents();
    },
    clearDates() {
      this.dateRange = [];
      this.fetchEvents();
    },
  },
};
</script>

<template>
  <div>
    <!-- Toolbar -->
    <div class="is-flex is-align-items-center is-justify-content-space-between mb-5">
      <div class="is-flex is-align-items-center" style="gap: 8px;">
        <b-field class="mb-0">
          <b-radio-button v-model="viewMode" native-value="calendar">
            <b-icon icon="calendar" size="is-small" />
            <span>Vue calendrier</span>
          </b-radio-button>
          <b-radio-button v-model="viewMode" native-value="list">
            <b-icon icon="list" size="is-small" />
            <span>Vue liste</span>
          </b-radio-button>
        </b-field>
        <b-button
          v-if="viewMode === 'list'"
          :type="showFilters ? 'is-light' : ''"
          icon-left="filter"
          @click="showFilters = !showFilters"
        >
          Filtres
        </b-button>
      </div>
      <b-button type="is-primary" icon-left="plus" @click="openCreateModal">
        Créer un nouvel événement
      </b-button>
    </div>

    <!-- Filters (list mode, toggled) -->
    <div v-if="viewMode === 'list' && showFilters" class="columns mb-4">
      <div class="column">
        <b-field label="Filtrer par">
          <b-input
            v-model="searchQuery"
            placeholder="Rechercher par nom..."
            :loading="isFetching"
            icon="search"
            expanded
            @input="onFiltersChange"
          />
        </b-field>
      </div>
      <div class="column is-narrow">
        <b-field label="Statut">
          <b-select v-model="statusFilter" @input="onFiltersChange">
            <option
              v-for="opt in STATUS_OPTIONS"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-narrow">
        <b-field label="Fréquence">
          <b-select v-model="frequencyFilter" @input="onFiltersChange">
            <option
              v-for="opt in FREQUENCY_OPTIONS"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-narrow">
        <b-field label="Dates">
          <b-datepicker
            v-model="dateRange"
            :mobile-native="false"
            range
            @input="onDateChange"
          >
            <b-button
              label="Effacer"
              type="is-danger"
              icon-left="times"
              outlined
              expanded
              @click="clearDates"
            />
          </b-datepicker>
        </b-field>
      </div>
      <div class="column is-narrow">
        <b-field label="Trier par">
          <b-select v-model="sortBy" @input="onSortChange">
            <option
              v-for="opt in SORT_OPTIONS"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </b-select>
          <p class="control">
            <b-tooltip :label="sortDir === 'asc' ? 'croissant' : 'décroissant'">
              <b-button
                :icon-left="sortDir === 'asc' ? 'arrow-up' : 'arrow-down'"
                @click="toggleSortDir"
              />
            </b-tooltip>
          </p>
        </b-field>
      </div>
    </div>

    <!-- Monthly calendar view (default) -->
    <template v-if="viewMode === 'calendar'">
      <CalendarGrid
        :events="events"
        :month="calendarMonth"
        :year="calendarYear"
        @month-change="onCalendarMonthChange"
        @event-click="openDetailModal"
      />
    </template>

    <!-- List view -->
    <template v-if="viewMode === 'list'">
      <template v-if="isFetching && events.length === 0">
        <div>
          <b-skeleton height="100" class="mb-3" />
          <b-skeleton height="100" class="mb-3" />
          <b-skeleton height="100" class="mb-3" />
        </div>
      </template>
      <template v-else-if="events.length === 0">
        <EmptyState
          title="Aucun événement"
          text="Cliquez sur le bouton ci-dessus pour créer votre premier événement."
          cta-text="Créer un événement"
          :cta-func="openCreateModal"
        />
      </template>
      <template v-else>
        <CalendarEventItem
          v-for="event in paginatedEvents"
          :key="event.uuid"
          :event="event"
          class="mb-3"
          @delete="fetchEvents"
          @update="fetchEvents"
          @edit="openEditModal"
          @view="openDetailModal"
        />
        <b-pagination
          v-if="events.length > PER_PAGE"
          v-model="currentPage"
          :total="events.length"
          :per-page="PER_PAGE"
          order="is-right"
          class="mt-4"
          aria-next-label="Page suivante"
          aria-previous-label="Page précédente"
          aria-page-label="Page"
          aria-current-label="Page actuelle"
        />
      </template>
    </template>

    <!-- Mobile CTA -->
    <b-button
      class="is-hidden-tablet mt-5"
      type="is-primary"
      icon-left="plus"
      expanded
      @click="openCreateModal"
    >
      Créer un événement
    </b-button>
  </div>
</template>
